import { IconButton } from '@/shared/ui';
import { XCircleIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EmployeeInGroupResponse, useEmployeeGroup, useRemoveEmployeesFromGroup } from '@/entities/employeeGroups';
import { NestedItemCard } from '@/features/nested-items';
import { AddOperatorsDialog } from './AddOperatorsDialog';
import { getEmployeeName } from './employeeName';

type Props = {
  groupId: number;
  disabled?: boolean;
};

/**
 * Список операторов в группе с возможностью добавления и удаления.
 * Данные приходят из GET /employeeGroup/{id}; мутации инвалидируют detail-кеш группы.
 */
export const EmployeesList: FC<Props> = ({ groupId, disabled }) => {
  const { t } = useTranslation(['common', 'employees']);
  const { data } = useEmployeeGroup(groupId);
  const { mutateAsync: removeEmployees, isPending: isRemoving } = useRemoveEmployeesFromGroup();

  const employees = data?.employees ?? [];

  const handleRemoveEmployee = async (employeeId: number) => {
    await removeEmployees({ id: groupId, body: { employeeIds: [employeeId] } });
  };

  const renderExternalId = (employee: EmployeeInGroupResponse) =>
    employee.externalId && `${t('employees:externalId')}: ${employee.externalId}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-center">
        <p className="font-medium">{t('employees:groups.operators')}</p>
        {!disabled && (
          <AddOperatorsDialog groupId={groupId} existingEmployeeIds={employees.map((employee) => employee.id)} />
        )}
      </div>

      <div className="flex flex-col gap-3">
        {employees.map((employee) => (
          <NestedItemCard
            key={employee.id}
            item={employee}
            renderTitle={getEmployeeName}
            renderDescription={renderExternalId}
            action={
              !disabled && (
                <IconButton
                  icon={<XCircleIcon />}
                  variant="ghost"
                  color="destructive"
                  isLoading={isRemoving}
                  label={t('employees:groups.removeOperatorFromGroup')}
                  onClick={() => handleRemoveEmployee(employee.id)}
                />
              )
            }
          />
        ))}
        {employees.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('employees:groups.noOperatorsInGroup')}</p>
        )}
      </div>
    </div>
  );
};
